import { computed, CSSProperties, onMounted, Ref, ref } from 'vue'
import {
  BASE_LIGHTING_TARGETS,
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_EFFECT_KIND,
  LIGHTING_TARGET,
  type BaseCurrentLightEffect,
  type BaseLightEffect,
  type BaseLightingTarget,
  type LightingCapability,
  type LightingEffectDefinition,
  type LightingTarget,
  type RGB,
  type RGBGroup,
  type lightingColor,
  type BaseChargeLightDisplayMode,
} from '@rapoo/device-sdk'
import {
  useDevicePreferencesStore,
  useDeviceStore,
  useLocaleStore,
  type DeviceIdentifier,
} from '@/stores'
import { rgbToHex } from '@/utils/color'
import { getBaseLighting } from '../utils/pages'
import { defaultReportError } from '@/composables/useErrorLogger'

/** UI 灯效配置 */
export interface BaseLightingEffect {
  id: number
  i18nKey: string
  target: LightingTarget
  kind: LightingEffectDefinition['kind']
  editableFields: LightingEffectDefinition['editableFields']
  tip?: string
  icon: string
  name: string
  styles: CSSProperties
}

/** 开关关闭后冻结的"展示中"灯效信息：铭牌灯在开关关闭后仍显示，需保留关闭前的展示状态 */
interface BaseLightingDisplayState {
  currentEffectId: number
  brightness: number
  speed: number
  rgb: RGB
}

/** 灯光类型的灯效灯光信息 */
interface BaseLightingTargetState {
  effects: BaseLightingEffect[]
  currentEffectId: number
  brightness: number
  speed: number
  rgb: RGB
  rgbGroup: RGBGroup
  gradientDirection: number
  currentEffectConfig?: BaseLightEffect
  /** 开关关闭期间冻结的展示状态（仅铭牌灯），用于预览与滑块展示 */
  displayed?: BaseLightingDisplayState
}

const BATTERY_COLOR = [1, 255, 133] as [number, number, number]
const BATTERY_INDICATRO_MODE = 6

/** 默认初始化灯效配置 */
function createFallbackEffect(target: LightingTarget): BaseLightingEffect {
  return {
    id: 0,
    i18nKey: '',
    target,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [],
    icon: 'LightEffect-Wireframe-DynamicLight',
    name: '',
    styles: {},
  }
}

/** 创建默认的灯光类型 */
function createDefaultTargetState(): BaseLightingTargetState {
  return {
    effects: [],
    currentEffectId: 1,
    brightness: 60,
    speed: 60,
    rgb: { r: 255, g: 37, b: 88 },
    rgbGroup: [
      { r: 255, g: 37, b: 88 },
      { r: 255, g: 37, b: 88 },
    ],
    gradientDirection: 1,
    currentEffectConfig: undefined,
  }
}

/** 获取灯光的icon */
function getBaseEffectIcon(effect: LightingEffectDefinition): string {
  if (effect.kind === LIGHTING_EFFECT_KIND.Indicator) return 'LightEffect-Wireframe-ChargeLight'
  if (
    effect.kind === LIGHTING_EFFECT_KIND.Static ||
    effect.kind === LIGHTING_EFFECT_KIND.Gradient
  ) {
    return 'LightEffect-Wireframe-StaticLight'
  }
  return 'LightEffect-Wireframe-DynamicLight'
}

function colorTupleToRgb(color: [number, number, number]): RGB {
  return { r: color[0], g: color[1], b: color[2] }
}

function rgbToColorTuple(rgb: RGB): [number, number, number] {
  return [rgb.r, rgb.g, rgb.b]
}

/** 灯带排布方向：横放底座近乎水平，ESPR5 等竖放底座沿竖直边 */
type LightBandOrientation = 'horizontal' | 'vertical'

function getLightBandOrientation(): LightBandOrientation {
  const model = useDeviceStore().currentDevice?.manifest.model
  // ESPR5 底座竖放，灯带沿左侧竖边；其余底座灯带近乎水平
  return model === 'ESPR5' ? 'vertical' : 'horizontal'
}

interface BaseEffectStyleOptions {
  rgbGroup?: RGBGroup
  gradientDirection?: number
}

/** 根据 manifest.capabilities中的灯效effects构建为 UI 适配的配置 */
function mapBaseLightingEffects(
  effectList: LightingEffectDefinition[],
  rgb: RGB,
  translate: (key: string) => string,
  orientation: LightBandOrientation = 'horizontal',
  styleOptions?: BaseEffectStyleOptions,
): BaseLightingEffect[] {
  return effectList.map((effect) => ({
    ...effect,
    icon: getBaseEffectIcon(effect),
    name: translate(effect.i18nKey),
    styles: getBaseEffectStyles(effect, rgb, orientation, styleOptions),
  }))
}

/** 根据灯效类型构建显示的样式 */
function getBaseEffectStyles(
  effect: LightingEffectDefinition,
  rgb: RGB,
  orientation: LightBandOrientation = 'horizontal',
  styleOptions?: BaseEffectStyleOptions,
): CSSProperties {
  const isVertical = orientation === 'vertical'
  const colorfulGradient = `linear-gradient(${isVertical ? '180deg' : '90deg'}, #ff0040, #ff8a00, #ffe600, #00e676, #00b0ff, #7c4dff, #ff0040)`

  if (effect.i18nKey.includes('battery_indicator')) {
    return { background: rgbToHex(colorTupleToRgb(BATTERY_COLOR)) }
  }
  if (effect.i18nKey.includes('static_breathing')) {
    return {
      animation: 'breathe 3s ease-in-out infinite',
      background: rgbToHex(rgb),
    }
  }
  if (effect.i18nKey.includes('color_breathing')) {
    return {
      animation: 'colorCycle 27s linear infinite, breathe 3s ease-in-out infinite',
      backgroundColor: '#FF0000',
    }
  }
  if (effect.editableFields.includes(LIGHTING_EDITABLE_FIELD.Gradient)) {
    const [c1, c2] = styleOptions?.rgbGroup ?? [rgb, rgb]
    const dir = styleOptions?.gradientDirection === 2 ? 'bottom' : 'right'
    return {
      background: `linear-gradient(to ${dir}, rgb(${c1.r},${c1.g},${c1.b}), rgb(${c2.r},${c2.g},${c2.b}))`,
    }
  }
  if (effect.i18nKey.includes('static')) {
    return { background: rgbToHex(rgb) }
  }
  if (effect.editableFields.includes(LIGHTING_EDITABLE_FIELD.ColorCycle)) {
    return effect.id === 1
      ? {
          background: colorfulGradient,
          backgroundSize: isVertical ? '100% 200%' : '200% 100%',
          animation: isVertical ? 'flow-vertical 3s linear infinite' : 'flow 3s linear infinite',
        }
      : { background: colorfulGradient }
  }
  return { background: rgbToHex(rgb) }
}

function effectConfigToState(
  effectConfig: BaseLightEffect,
): Pick<
  BaseLightingTargetState,
  'brightness' | 'speed' | 'rgb' | 'rgbGroup' | 'gradientDirection' | 'currentEffectConfig'
> {
  const primaryColor =
    effectConfig.mode === BATTERY_INDICATRO_MODE
      ? BATTERY_COLOR
      : effectConfig.rgbGroup?.[0]
        ? rgbToColorTuple(effectConfig.rgbGroup[0])
        : effectConfig.color
  const rgbGroup =
    effectConfig.rgbGroup && effectConfig.rgbGroup.length > 0
      ? effectConfig.rgbGroup
      : [colorTupleToRgb(effectConfig.color), colorTupleToRgb(effectConfig.color)]

  return {
    brightness: effectConfig.brightness,
    speed: effectConfig.frequency,
    rgb: colorTupleToRgb(primaryColor),
    rgbGroup,
    gradientDirection: effectConfig.gradientDirection ?? 1,
    currentEffectConfig: effectConfig,
  }
}

/** 获取当前设备的 vid/pid 标识，用于本地持久化 */
function getDeviceIdentifier(): DeviceIdentifier | undefined {
  const device = useDeviceStore().currentDevice
  if (!device) return
  return {
    vid: device.manifest.hardware.vendorId,
    pid: device.manifest.hardware.productId,
  }
}

/** 获取当前设备可用的灯效类型 */
function getAvailableLightingTargets(): BaseLightingTarget[] {
  const deviceStore = useDeviceStore()
  const lighting = deviceStore.currentDevice?.manifest.capabilities.find(
    (item) => item.type === 'lighting',
  ) as LightingCapability | undefined

  if (!lighting) {
    return [LIGHTING_TARGET.BaseLightBand]
  }

  const targets = BASE_LIGHTING_TARGETS.filter((lightTarget) =>
    lighting.config.effects.some((effect) => effect.target === lightTarget),
  )

  return targets.length > 0 ? targets : [LIGHTING_TARGET.BaseLightBand]
}

/** 获取目标灯效的灯光信息 */
async function loadTargetState(
  lightingTarget: BaseLightingTarget,
  translate: (key: string) => string,
): Promise<{ state: BaseLightingTargetState; current: BaseCurrentLightEffect }> {
  const baseLighting = getBaseLighting()!

  const effectList = await baseLighting.getEffects(lightingTarget)
  const current = await baseLighting.getCurrentEffect(lightingTarget)
  const orientation = getLightBandOrientation()
  const effects = mapBaseLightingEffects(
    effectList,
    createDefaultTargetState().rgb,
    translate,
    orientation,
  )
  const currentEffect = effectList.find((effect) => effect.id === current.modeId)
  const currentEffectExists = current.modeId > 0 && Boolean(currentEffect)

  // 无有效当前灯效时不读 getLightEffect，避免 ESPR5 等设备无 mode 时报错
  if (!currentEffectExists) {
    return {
      state: {
        ...createDefaultTargetState(),
        currentEffectId: 0,
        effects,
      },
      current,
    }
  }

  const configState = effectConfigToState(
    await baseLighting.getLightEffect(current.modeId, lightingTarget),
  )

  return {
    state: {
      ...createDefaultTargetState(),
      ...configState,
      currentEffectId: current.modeId,
      effects: mapBaseLightingEffects(effectList, configState.rgb, translate, orientation),
    },
    current,
  }
}

/** 底座灯光 */
export function useBaseLighting(target: Ref<BaseLightingTarget>) {
  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const devicePreferencesStore = useDevicePreferencesStore()
  /** 灯带总开关 */
  const enabledLighting = ref(true)
  /**
   * P6 PLUS 暂时隐藏灯效总开关。
   * 该机 0xC3 回包读到的开关状态不可信，界面会一直显示为关且无法打开。
   * 协议确认后再恢复；隐藏期间预览按灯效正常展示，不跟随错误的关灯状态。
   */
  const showLightingSwitch = computed(() => deviceStore.currentDevice?.manifest.model !== 'P6 PLUS')
  /** 充电时显示氛围灯还是充电灯，灯带与铭牌灯共享 */
  const chargingState = ref<BaseChargeLightDisplayMode>('ambient')
  const lightingByTarget = ref<Partial<Record<BaseLightingTarget, BaseLightingTargetState>>>({})
  /** 灯带与铭牌灯同步灯效开关（仅前端功能，修改任一灯区时同步应用到另一灯区） */
  const syncLighting = ref(false)
  const loading = ref(false)

  const effects = computed(() => getActiveState().effects)

  const currentEffect = computed<BaseLightingEffect>(() => getEffectForTarget(target.value))

  /** 某灯区"展示中"的灯效：开关关闭期间铭牌灯取冻结快照，其余情况取选中灯效 */
  const getDisplayEffectForTarget = (lightingTarget: BaseLightingTarget): BaseLightingEffect => {
    const state = lightingByTarget.value[lightingTarget]
    if (!state) return createFallbackEffect(lightingTarget)

    const displayed = state.displayed
    const effect =
      state.effects.find(
        (effect) => effect.id === (displayed?.currentEffectId ?? state.currentEffectId),
      ) ??
      state.effects[0] ??
      createFallbackEffect(lightingTarget)
    return {
      ...effect,
      styles: getBaseEffectStyles(effect, displayed?.rgb ?? state.rgb, getLightBandOrientation(), {
        rgbGroup: state.rgbGroup,
        gradientDirection: state.gradientDirection,
      }),
    }
  }

  /** 各灯区当前展示的灯效样式，供预览组件分别渲染灯带与铭牌灯 */
  const previewEffectStyles = computed<Partial<Record<BaseLightingTarget, CSSProperties>>>(() => ({
    [LIGHTING_TARGET.BaseLightBand]: getDisplayEffectForTarget(LIGHTING_TARGET.BaseLightBand)
      .styles,
    [LIGHTING_TARGET.BaseLightScreen]: getDisplayEffectForTarget(LIGHTING_TARGET.BaseLightScreen)
      .styles,
  }))

  const isColorful = computed(() => {
    return hasLightingEditableField(currentEffect.value, LIGHTING_EDITABLE_FIELD.ColorCycle)
  })

  const canEditBrightness = computed(() => {
    return hasLightingEditableField(currentEffect.value, LIGHTING_EDITABLE_FIELD.Brightness)
  })

  const canEditSpeed = computed(() => {
    return hasLightingEditableField(currentEffect.value, LIGHTING_EDITABLE_FIELD.Speed)
  })

  const canEditGradient = computed(() => {
    return hasLightingEditableField(currentEffect.value, LIGHTING_EDITABLE_FIELD.Gradient)
  })

  /** 取当前灯区"展示中"的状态：开关关闭期间铭牌灯冻结为关闭前的快照，其余情况等于选中状态 */
  const getDisplayedActiveState = (): BaseLightingTargetState => {
    return getActiveState()
  }

  const brightness = computed({
    get: () => getDisplayedActiveState().brightness,
    set: (value) => updateActiveState({ brightness: value }),
  })

  const speed = computed({
    get: () => getDisplayedActiveState().speed,
    set: (value) => updateActiveState({ speed: value }),
  })

  const rgbGroup = computed({
    get: () => getActiveState().rgbGroup,
    set: (value) => updateActiveState({ rgbGroup: value }),
  })

  const gradientDirection = computed({
    get: () => getActiveState().gradientDirection,
    set: (value) => updateActiveState({ gradientDirection: value }),
  })

  const rgb = computed({
    get: () => getDisplayedActiveState().rgb,
    set: (value) => updateActiveState({ rgb: value }),
  })

  const getActiveState = () => lightingByTarget.value[target.value] ?? createDefaultTargetState()

  const updateTargetState = (
    lightingTarget: BaseLightingTarget,
    patch: Partial<BaseLightingTargetState>,
  ) => {
    lightingByTarget.value = {
      ...lightingByTarget.value,
      [lightingTarget]: {
        ...(lightingByTarget.value[lightingTarget] ?? createDefaultTargetState()),
        ...patch,
      },
    }
  }

  const updateActiveState = (patch: Partial<BaseLightingTargetState>) => {
    updateTargetState(target.value, patch)
  }

  const getEffectForTarget = (lightingTarget: BaseLightingTarget): BaseLightingEffect => {
    const state = lightingByTarget.value[lightingTarget] ?? createDefaultTargetState()
    return (
      state.effects.find((effect) => effect.id === state.currentEffectId) ??
      createFallbackEffect(lightingTarget)
    )
  }

  const getOtherTarget = (lightingTarget: BaseLightingTarget): BaseLightingTarget => {
    return lightingTarget === LIGHTING_TARGET.BaseLightBand
      ? LIGHTING_TARGET.BaseLightScreen
      : LIGHTING_TARGET.BaseLightBand
  }

  /** 将某灯区当前的选中灯效（模式+亮度/速度/颜色）同步应用到另一灯区 */
  const syncEffectToOtherTarget = async (source: BaseLightingTarget) => {
    const baseLighting = getBaseLighting()
    const other = getOtherTarget(source)
    const sourceState = lightingByTarget.value[source]
    const otherState = lightingByTarget.value[other]
    if (!baseLighting || !sourceState || !otherState) return

    const { currentEffectId, brightness, speed, rgb, rgbGroup, gradientDirection } = sourceState
    if (!otherState.effects.some((effect) => effect.id === currentEffectId)) return

    try {
      await baseLighting.setCurrentEffect(currentEffectId, other)
      await baseLighting.setLightEffect({
        mode: currentEffectId,
        brightness,
        frequency: speed,
        color: rgbToColorTuple(rgb),
        gradientDirection,
        rgbGroup,
        target: other,
      })
      updateTargetState(other, {
        currentEffectId,
        brightness,
        speed,
        rgb,
        rgbGroup,
        gradientDirection,
      })
    } catch (err) {
      console.error('Failed to sync lighting effect', err)
      defaultReportError(`Failed to sync lighting effect: ${JSON.stringify(err)}`)
    }
  }

  const init = async () => {
    loading.value = true
    const baseLighting = getBaseLighting()
    if (!baseLighting) return

    try {
      const targets = getAvailableLightingTargets()
      const results = await Promise.all(
        targets.map(async (lightingTarget) => {
          const result = await loadTargetState(lightingTarget, t)
          return [lightingTarget, result] as const
        }),
      )

      const nextState: Partial<Record<BaseLightingTarget, BaseLightingTargetState>> = {}
      for (const [lightingTarget, { state }] of results) {
        nextState[lightingTarget] = state
      }

      const activeEntry =
        results.find(([lightingTarget]) => lightingTarget === target.value) ?? results[0]
      if (activeEntry) {
        enabledLighting.value = activeEntry[1].current.isOn
        chargingState.value = activeEntry[1].current.chargeLightDisplayMode ?? 'ambient'

        const identifier = getDeviceIdentifier()
        syncLighting.value = identifier
          ? (devicePreferencesStore.getLightingSync(identifier) ?? false)
          : false

        // 开关关闭时铭牌灯仍显示，刷新后预览需继续展示关闭前的灯效
        if (!enabledLighting.value) {
          const snapshot = identifier
            ? devicePreferencesStore.getLightingDisplay(identifier)
            : undefined
          const screenState = nextState[LIGHTING_TARGET.BaseLightScreen]
          if (snapshot && screenState) {
            screenState.displayed = snapshot
          }
        }
      }
      lightingByTarget.value = nextState
    } catch (err) {
      console.error('Failed to init lighting', err)
      defaultReportError(`Failed to init lighting: ${JSON.stringify(err)}`)
    } finally {
      loading.value = false
    }
  }

  const setLightingEnabled = async (enabled: boolean) => {
    const baseLighting = getBaseLighting()
    if (!baseLighting) return

    try {
      await baseLighting.switchLight(enabled)
      enabledLighting.value = enabled

      if (enabled) {
        // 打开开关：铭牌灯立即恢复展示最后一次设置的灯效，与选中状态同步
        if (lightingByTarget.value[LIGHTING_TARGET.BaseLightScreen]?.displayed) {
          updateTargetState(LIGHTING_TARGET.BaseLightScreen, { displayed: undefined })
        }
        const identifier = getDeviceIdentifier()
        if (identifier) devicePreferencesStore.removeLightingDisplay(identifier)
      } else {
        // 关闭开关：灯带关闭、铭牌灯仍显示，冻结当前展示状态，刷新后预览仍显示关闭前的灯效
        const screenState = lightingByTarget.value[LIGHTING_TARGET.BaseLightScreen]
        if (screenState) {
          const displayed = {
            currentEffectId: screenState.currentEffectId,
            brightness: screenState.brightness,
            speed: screenState.speed,
            rgb: screenState.rgb,
          }
          updateTargetState(LIGHTING_TARGET.BaseLightScreen, { displayed })
          const identifier = getDeviceIdentifier()
          if (identifier) devicePreferencesStore.setLightingDisplay(identifier, displayed)
        }
      }
    } catch (err) {
      console.error('Failed to set lighting switch', err)
      defaultReportError(`Failed to set lighting switch: ${JSON.stringify(err)}`)
    }
  }

  const updateSyncLighting = async (enabled: boolean) => {
    syncLighting.value = enabled
    const identifier = getDeviceIdentifier()
    if (identifier) devicePreferencesStore.setLightingSync(identifier, enabled)

    // 打开同步时立即以当前页签的灯效同步到另一灯区
    if (enabled) {
      await syncEffectToOtherTarget(target.value)
    }
  }

  const setEffect = async (effect: BaseLightingEffect) => {
    const baseLighting = getBaseLighting()
    if (!baseLighting) return

    try {
      await baseLighting.setCurrentEffect(effect.id, target.value)
      const nextState = effectConfigToState(
        await baseLighting.getLightEffect(effect.id, target.value),
      )
      updateActiveState({
        currentEffectId: effect.id,
        ...nextState,
      })
      if (syncLighting.value) {
        await syncEffectToOtherTarget(target.value)
      }
    } catch (err) {
      console.error('Failed to set effect', err)
      defaultReportError(`Failed to set effect: ${JSON.stringify(err)}`)
    }
  }

  const persistLightEffect = async () => {
    const baseLighting = getBaseLighting()
    if (!baseLighting) return

    // 始终写入"选中"状态（开关关闭期间展示状态冻结，写入的是用户最后选择的值）
    const state = getActiveState()
    try {
      await baseLighting.setLightEffect({
        mode: state.currentEffectId,
        brightness: state.brightness,
        frequency: state.speed,
        color: rgbToColorTuple(state.rgb),
        gradientDirection: state.gradientDirection,
        rgbGroup: state.rgbGroup,
        target: target.value,
      })

      if (syncLighting.value) {
        const other = getOtherTarget(target.value)
        const otherState = lightingByTarget.value[other]
        if (otherState) {
          await baseLighting.setLightEffect({
            mode: otherState.currentEffectId,
            brightness: state.brightness,
            frequency: state.speed,
            color: rgbToColorTuple(state.rgb),
            gradientDirection: state.gradientDirection,
            rgbGroup: state.rgbGroup,
            target: other,
          })
          updateTargetState(other, {
            brightness: state.brightness,
            speed: state.speed,
            rgb: state.rgb,
            rgbGroup: state.rgbGroup,
            gradientDirection: state.gradientDirection,
          })
        }
      }
    } catch (err) {
      console.error('Failed to set light effect', err)
      defaultReportError(`Failed to set light effect: ${JSON.stringify(err)}`)
    }
  }

  const setBrightness = async (value: number) => {
    brightness.value = value
    await persistLightEffect()
  }

  const setSpeed = async (value: number) => {
    speed.value = value
    await persistLightEffect()
  }

  const setColor = async (color: lightingColor) => {
    if (color === 'linear' || Array.isArray(color)) return

    await persistLightEffect()
  }

  /** 切换左右/上下渐变方向，并同步双色起止点 */
  const setGradient = async (data: { rgbGroup: RGBGroup; direction: number }) => {
    updateActiveState({
      rgbGroup: data.rgbGroup,
      gradientDirection: data.direction,
      rgb: data.rgbGroup[0] ?? getActiveState().rgb,
    })
    await persistLightEffect()
  }

  const setChargingLight = async (state: BaseChargeLightDisplayMode) => {
    const baseLighting = getBaseLighting()
    if (!baseLighting) return

    try {
      await baseLighting.setChargeLightDisplayMode(state)
      chargingState.value = state
    } catch (err) {
      console.error('Failed to set light effect', err)
      defaultReportError(`Failed to set light effect: ${JSON.stringify(err)}`)
    }
  }

  onMounted(() => {
    init()
  })

  return {
    effects,
    enabledLighting,
    showLightingSwitch,
    chargingState,
    syncLighting,
    currentEffect,
    brightness,
    speed,
    rgb,
    loading,
    isColorful,
    previewEffectStyles,
    canEditBrightness,
    canEditSpeed,
    canEditGradient,
    rgbGroup,
    gradientDirection,
    setLightingEnabled,
    updateSyncLighting,
    setEffect,
    setBrightness,
    setSpeed,
    setColor,
    setGradient,
    setChargingLight,
  }
}
